import React, { useState, useEffect, useCallback } from 'react';
import { TopicCategory, TOPIC_CATEGORY_LABELS, getAllTopicCategories } from '../constants/topicCategories';
import SimilarityCheckStep from './SimilarityCheckStep';
import { checkSimilarQuestions, addObjectionVariation, addFAQVariation } from '../services/apiService';
import { Refresh as LoadingIcon } from '@mui/icons-material';

interface SaveInquiryModalProps {
  inquiry: {
    question: string;
    answer?: string;
    category: string;
  };
  onClose: () => void;
  onSave: (
    inquiry: any,
    type: 'objection' | 'faq',
    editedData: { question: string; answer: string; category: string }
  ) => Promise<void>;
  onMarkAsProcessed?: (inquiry: any) => Promise<void>;
  isSaving: boolean;
}

const SaveInquiryModal: React.FC<SaveInquiryModalProps> = ({
  inquiry,
  onClose,
  onSave,
  onMarkAsProcessed,
  isSaving
}) => {
  const [step, setStep] = useState<'checking' | 'similar-found' | 'no-similar' | 'edit'>('checking');
  const [saveType, setSaveType] = useState<'objection' | 'faq'>('objection');
  const [editedQuestion, setEditedQuestion] = useState(inquiry.question);
  const [editedAnswer, setEditedAnswer] = useState(inquiry.answer || '');
  const [selectedTopicCategory, setSelectedTopicCategory] = useState<TopicCategory>(TopicCategory.OTHER);
  const [similarQuestions, setSimilarQuestions] = useState<any[]>([]);
  const [isCheckingSimilarity, setIsCheckingSimilarity] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkForSimilarQuestionsCallback = useCallback(async (questionToCheck?: string) => {
    setIsCheckingSimilarity(true);
    setError(null);

    try {
      const result: any = await checkSimilarQuestions(questionToCheck || editedQuestion || inquiry.question, 'both', 0.5);

      if (result && result.similar && result.similar.length > 0) {
        setSimilarQuestions(result.similar);
        setStep('similar-found');
      } else {
        setStep('no-similar');
      }
    } catch (err: any) {
      console.error('Error checking similar questions:', err);
      setError('Failed to check for similar questions');
      setStep('no-similar'); // Proceed anyway on error
    } finally {
      setIsCheckingSimilarity(false);
    }
  }, [editedQuestion, inquiry.question]);

  useEffect(() => {
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';

    // Check for similar questions when modal opens
    checkForSimilarQuestionsCallback();

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [checkForSimilarQuestionsCallback]);

  // Re-check similar questions when the edited question changes significantly
  const handleQuestionChangeInSimilarity = (newQuestionText: string) => {
    setEditedQuestion(newQuestionText);
  };

  const handleSave = async () => {
    if (!editedQuestion.trim() || !editedAnswer.trim()) {
      alert('Question and answer are required');
      return;
    }

    await onSave(inquiry, saveType, {
      question: editedQuestion.trim(),
      answer: editedAnswer.trim(),
      category: selectedTopicCategory
    });
  };

  const handleUseExisting = async (existingQuestion: any) => {
    // Just mark the inquiry as processed without adding to database
    if (onMarkAsProcessed) {
      await onMarkAsProcessed(inquiry);
    }
    onClose();
  };

  const handleAddAsVariation = async (parentQuestion: any) => {
    try {
      // Use the edited question for the variation
      const questionText = editedQuestion || inquiry.question;
      if (parentQuestion.type === 'objection') {
        await addObjectionVariation(parentQuestion._id, questionText);
      } else {
        await addFAQVariation(parentQuestion._id, questionText);
      }

      // Mark as processed
      if (onMarkAsProcessed) {
        await onMarkAsProcessed(inquiry);
      }

      onClose();
    } catch (err: any) {
      setError('Failed to add as variation');
      console.error(err);
    }
  };

  const handleAddAsNew = () => {
    setStep('no-similar');
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[1300] flex items-center justify-center">
      <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            {step === 'checking' && 'Checking for Similar Questions...'}
            {step === 'similar-found' && 'Similar Questions Found'}
            {(step === 'no-similar' || step === 'edit') && 'Add New Inquiry to Database'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
            disabled={isSaving || isCheckingSimilarity}
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {/* Show error message if any */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Show loading state while checking */}
          {step === 'checking' && (
            <div className="flex flex-col items-center justify-center py-12">
              <LoadingIcon className="animate-spin text-blue-600 mb-4" style={{ fontSize: '3rem' }} />
              <p className="text-gray-600">Checking for similar questions...</p>
            </div>
          )}

          {/* Show similar questions if found */}
          {step === 'similar-found' && (
            <SimilarityCheckStep
              similarQuestions={similarQuestions}
              newQuestion={inquiry.question}
              editedQuestion={editedQuestion}
              onQuestionChange={handleQuestionChangeInSimilarity}
              onUseExisting={handleUseExisting}
              onAddAsVariation={handleAddAsVariation}
              onAddAsNew={handleAddAsNew}
              onCancel={onClose}
              isProcessing={isSaving}
            />
          )}

          {/* Show the normal form if no similar questions or user wants to add as new */}
          {(step === 'no-similar' || step === 'edit') && (
            <>
              <div className="space-y-4">
                {/* Save Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Save as:
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="objection"
                        checked={saveType === 'objection'}
                        onChange={(e) => setSaveType(e.target.value as 'objection')}
                        className="mr-2"
                      />
                      <span className="text-sm">Objection</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="faq"
                        checked={saveType === 'faq'}
                        onChange={(e) => setSaveType(e.target.value as 'faq')}
                        className="mr-2"
                      />
                      <span className="text-sm">FAQ</span>
                    </label>
                  </div>
                </div>

                {/* Topic Category Selection */}
                <div>
                  <label htmlFor="topicCategory" className="block text-sm font-medium text-gray-700 mb-2">
                    Topic Category:
                  </label>
                  <select
                    id="topicCategory"
                    value={selectedTopicCategory}
                    onChange={(e) => setSelectedTopicCategory(e.target.value as TopicCategory)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {getAllTopicCategories().map(cat => (
                      <option key={cat} value={cat}>
                        {TOPIC_CATEGORY_LABELS[cat]}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Question Field */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="question" className="block text-sm font-medium text-gray-700">
                      Question:
                    </label>
                    {editedQuestion !== inquiry.question && (
                      <button
                        type="button"
                        onClick={() => {
                          setStep('checking');
                          checkForSimilarQuestionsCallback(editedQuestion);
                        }}
                        disabled={isCheckingSimilarity || !editedQuestion.trim()}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        🔍 Re-check for similar questions
                      </button>
                    )}
                  </div>
                  <textarea
                    id="question"
                    value={editedQuestion}
                    onChange={(e) => setEditedQuestion(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter the question..."
                  />
                  {editedQuestion !== inquiry.question && (
                    <p className="mt-1 text-xs text-amber-600">
                      ✏️ Question has been modified from the original
                    </p>
                  )}
                </div>

                {/* Answer Field */}
                <div>
                  <label htmlFor="answer" className="block text-sm font-medium text-gray-700 mb-2">
                    Answer:
                  </label>
                  <textarea
                    id="answer"
                    value={editedAnswer}
                    onChange={(e) => setEditedAnswer(e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter the answer..."
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-700">
                    <strong>Note:</strong> You can add variations for this {saveType} later in the Knowledge Base tab.
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 p-4 border-t mt-6">
                <button
                  onClick={onClose}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    `Save as ${saveType === 'objection' ? 'Objection' : 'FAQ'}`
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SaveInquiryModal;